<template>
  <UIDialog
    :is-open
    :z-index
    size="3md"
    title="Image Cropper"
    flat
    @close="handleClose"
  >
    <div class="ui-image-cropper-modal">
      <VuePictureCropper
        :boxStyle="{
          width: '100%',
          height: '100%',
        }"
        :img="image"
        :options="{
          viewMode: 1,
          dragMode: 'crop',
          aspectRatio,
        }"
      />
    </div>
    <template #footer>
      <div class="ui-image-cropper-modal__buttons">
        <UIButton
          :leading="ArrowPathIcon"
          label="Reset"
          variant="secondary"
          fill="light"
          :size
          @click="handleReset"
        />
        <UIButton
          :leading="TrashIcon"
          label="Clear"
          variant="secondary"
          fill="light"
          :size
          @click="handleClear"
        />
        <div class="ui-image-cropper-modal__buttons-right">
          <UIButton
            label="Cancel"
            variant="gray"
            fill="outlined"
            :size
            @click="handleClose"
          />
          <UIButton
            :leading="CheckIcon"
            label="Apply"
            :size
            @click="handleApply"
          />
        </div>
      </div>
    </template>
  </UIDialog>
</template>

<script setup lang="ts">
import { computed } from 'vue'
import VuePictureCropper, { cropper } from 'vue-picture-cropper'

import { UIDialog } from '@ui/modals'

import { ModalClass } from '@types'
import { UIButton } from '@ui/buttons'
import { ArrowPathIcon, CheckIcon, TrashIcon } from '@heroicons/vue/24/outline'

type Props = {
  modal: ModalClass<any>
  image: string
  aspectRatio: number
  fileName?: string
}

type Emits = {
  apply: [base64: string, blob: Blob, file: File | null]
}

const props = defineProps<Props>()
const emit = defineEmits<Emits>()

const size = 'sm'

const isOpen = computed(() => props.modal.isOpened)
const zIndex = computed(() => props.modal.zIndex)

const handleClose = () => {
  props.modal.close()
}

const handleClear = () => {
  cropper?.clear()
}

const handleReset = () => {
  cropper?.reset()
}

const handleApply = async () => {
  if (!cropper) return
  const base64 = cropper.getDataURL()
  const blob: Blob | null = await cropper.getBlob()
  if (!blob) return

  const file = await cropper.getFile({
    fileName: props.fileName || 'cropped-image',
  })

  emit('apply', base64, blob, file)
  handleClose()
}
</script>

<style>
.ui-image-cropper-modal {
  @apply aspect-video;
  @apply bg-body-dark;
  @apply overflow-hidden;

  &__buttons {
    @apply flex items-center justify-between;
    @apply gap-2;
    @apply py-3 px-2 sm:px-default;
  }

  &__buttons-right {
    @apply flex items-center;
    @apply gap-2 ml-auto;
  }
}
</style>
